/**
 * chartTheme.jsx
 *
 * This module exports a consolidated theme object for charts. It centralizes design tokens
 * such as colors, typography, spacing, and component-specific styling (e.g., tooltips, grids,
 * axes, legends, and active dots). This ensures consistency and scalability across chart components.
 *
 * The theme leverages tokens from the application's design system, including typography and brand colors.
 * It also provides responsive settings with breakpoints and adjustments for different screen sizes.
 *
 * @module chartTheme
 */

// Import typography and colors from the design system.
import { typography } from './typography';

/**
 * The consolidated theme object for charts.
 *
 * @constant {object}
 */
export const chartTheme = {
    /**
     * Color tokens for chart elements.
     */
    colors: {
        primary: '#283FFF', // Primary color from the brand color palette.
        secondary: '#FF7557', // Secondary color from the brand color palette.
        background: 'none', // Default background color for charts (transparent).
        border: '#C5C7DA', // Light border color used for gridlines and tooltip borders.
        highlight: '#FF527A', // Color used for interactive elements (e.g., highlighting).
    },

    /**
     * Typography tokens imported directly from the design system.
     */
    typography,

    /**
     * Spacing tokens used for chart layout and component padding.
     */
    spacing: {
        chartPadding: 16, // General padding around chart components.
        legendMargin: 12, // Margin between the legend and the chart area.
        tooltipMargin: 8, // Margin around tooltips.

        // Additional spacing for specific components:
        legend: {
            paddingTop: 16,
            paddingBottom: 24,
        },
        tooltip: {
            padding: 12, // Overall tooltip padding.
            paddingX: 16, // Horizontal padding for tooltips.
            paddingY: 8, // Vertical padding for tooltips.
        },
    },

    /**
     * Styling for active dots (highlighted points) on charts.
     */
    activeDot: {
        r: 6, // Radius of the active dot.
        stroke: '#FFFFFF', // Stroke color for the active dot.
        strokeWidth: 3, // Stroke width for the active dot.
        dropShadow: {
            blur: 2, // Blur radius for the drop shadow.
            offsetX: 0, // Horizontal offset for the drop shadow.
            offsetY: 0, // Vertical offset for the drop shadow.
            color: 'rgba(0, 0, 0, 0.12)', // Color and opacity of the drop shadow.
        },
    },

    /**
     * Tooltip-specific tokens.
     */
    tooltip: {
        containerPadding: '12px', // Padding around the tooltip container.
        borderRadius: '12px', // Border radius for tooltip containers.
        boxShadow:
            '0px 1px 8px rgba(0, 0, 0, 0.12), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px -2px rgba(0, 0, 0, 0.20)',
        // Shadow effects for tooltips.
        backgroundColor: '#ffffff', // Background color for tooltips.
        borderColor: '#C5C7DA', // Border color for tooltips.
        textColor: typography.tooltip.textColor, // Text color from tooltip typography tokens.
        fontFamily: typography.tooltip.fontFamily, // Font family for tooltip text.
        fontSize: typography.tooltip.fontSize, // Font size for tooltip text.
        lineHeight: '1.25rem', // Line height for tooltip text.
    },

    /**
     * Grid styling tokens for charts.
     */
    grid: {
        stroke: '#C5C7DA', // Color used for gridlines.
    },

    /**
     * Axis styling tokens for charts.
     */
    axis: {
        stroke: '#444657', // Axis line color.
        strokeWidth: 0, // Width of the axis stroke (0 if using only text).
        color: '#444657', // Text color for axis labels.
    },

    /**
     * Legend styling tokens for charts.
     */
    legend: {
        iconSize: 10, // Size of the legend icon.
        iconType: 'square', // Shape of the legend icon.
        verticalAlign: 'top', // Vertical alignment of the legend.
        margin: { top: 10, bottom: 10 }, // Margin for the legend.
    },

    /**
     * Responsive design tokens.
     *
     * Provides breakpoints and adjustments for small, medium, and large screen sizes.
     */
    responsive: {
        breakpoints: {
            small: '480px',
            medium: '768px',
            large: '1024px',
        },
        adjustments: {
            small: {
                // Adjustments for small screens.
                legend: { paddingTop: 12, paddingBottom: 16 },
                tooltip: { fontSize: '0.75rem', padding: 8 },
            },
            medium: {
                // Add medium-specific adjustments if needed.
            },
            large: {
                // Add large-specific adjustments if needed.
            },
        },
    },
};

export const getChartTheme = (mode: 'light' | 'dark') => {
    if (mode === 'light') {
        return chartTheme;
    }

    return {
        ...chartTheme,
        colors: {
            ...chartTheme.colors,
            primary: 'var(--mui-palette-primary-main)',
            secondary: 'var(--mui-palette-secondary-main)',
            border: 'var(--mui-palette-elevation-outline-variant)',
            highlight: 'var(--mui-palette-error-main)',
        },
        tooltip: {
            ...chartTheme.tooltip,
            backgroundColor: 'var(--mui-palette-background-paper-elevation-8)',
            borderColor: 'var(--mui-palette-elevation-outline-variant)',
            textColor: 'var(--mui-palette-text-primary)',
        },
        grid: {
            ...chartTheme.grid,
            stroke: 'var(--mui-palette-elevation-outline-variant)',
        },
        axis: {
            ...chartTheme.axis,
            stroke: 'var(--mui-palette-text-secondary)',
            color: 'var(--mui-palette-text-secondary)',
        },
        typography: {
            ...chartTheme.typography,
            axis: {
                ...chartTheme.typography.axis,
                color: 'var(--mui-palette-text-secondary)',
            },
            legend: {
                ...chartTheme.typography.legend,
                color: 'var(--mui-palette-text-secondary)',
            },
            tooltip: {
                ...chartTheme.typography.tooltip,
                textColor: 'var(--mui-palette-text-primary)',
                title: {
                    ...chartTheme.typography.tooltip.title,
                    titleColor: 'var(--mui-palette-text-primary)',
                },
                label: {
                    ...chartTheme.typography.tooltip.label,
                    labelColor: 'var(--mui-palette-text-secondary)',
                },
                value: {
                    ...chartTheme.typography.tooltip.value,
                    valueColor: 'var(--mui-palette-text-primary)',
                    valueBackgroundColor: '',
                },
            },
        },
    };
};
